import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase';
import { PlusCircle, Edit, Trash2, Save, X, ArrowLeft } from 'lucide-react';
import { APP_VERSION } from '../../utils/constants';
import VariableInfo from '../../components/VariableInfo';

export default function PromptManagement() {
  // State for areas
  const [areas, setAreas] = useState([]);
  const [newArea, setNewArea] = useState('');
  const [editingArea, setEditingArea] = useState(null);
  const [editAreaName, setEditAreaName] = useState('');

  // State for prompts
  const [prompts, setPrompts] = useState([]);
  const [promptFilter, setPromptFilter] = useState('');
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [promptForm, setPromptForm] = useState({
    name: '',
    prompt: '',
    inverted_image: false,
    system_prompt: ''
  });

  // State for area-prompt relationships
  const [promptAreas, setPromptAreas] = useState([]);
  const [selectedPromptAreas, setSelectedPromptAreas] = useState([]);

  // UI state
  const [activeTab, setActiveTab] = useState('areas');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch data on component mount
  useEffect(() => {
    fetchAreas();
    fetchPrompts();
  }, []);

  // Fetch areas from Supabase
  const fetchAreas = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('areas')
        .select('*')
        .order('name');

      if (error) throw error;
      setAreas(data || []);
    } catch (error) {
      console.error('Error fetching areas:', error);
      setError('Failed to load areas');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch prompts from Supabase
  const fetchPrompts = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('prompts')
        .select('*')
        .order('name');

      if (error) throw error;
      setPrompts(data || []);
    } catch (error) {
      console.error('Error fetching prompts:', error);
      setError('Failed to load prompts');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch prompt areas relationship
  const fetchPromptAreas = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('areas_prompts')
        .select('*');

      if (error) throw error;
      setPromptAreas(data || []);
    } catch (error) {
      console.error('Error fetching prompt areas:', error);
      setError('Failed to load prompt areas relationships');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch areas for a specific prompt
  const fetchAreasForPrompt = async (promptId) => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('areas_prompts')
        .select('area_id')
        .eq('prompt_id', promptId);

      if (error) throw error;
      
      // Convert to array of area IDs
      const areaIds = data.map(item => item.area_id);
      setSelectedPromptAreas(areaIds);
    } catch (error) {
      console.error('Error fetching areas for prompt:', error);
      setError('Failed to load areas for this prompt');
    } finally {
      setIsLoading(false);
    }
  };

  // AREA CRUD OPERATIONS
  const addArea = async () => {
    if (!newArea.trim()) return;
    
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('areas')
        .insert([{ name: newArea.trim() }])
        .select();

      if (error) throw error;
      
      setAreas([...areas, data[0]]);
      setNewArea('');
    } catch (error) {
      console.error('Error adding area:', error);
      setError('Failed to add area');
    } finally {
      setIsLoading(false);
    }
  };

  const startEditArea = (area) => {
    setEditingArea(area.id);
    setEditAreaName(area.name);
  };

  const cancelEditArea = () => {
    setEditingArea(null);
    setEditAreaName('');
  };

  const updateArea = async () => {
    if (!editAreaName.trim()) return;
    
    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('areas')
        .update({ name: editAreaName.trim() })
        .eq('id', editingArea);

      if (error) throw error;
      
      setAreas(areas.map(area => 
        area.id === editingArea ? { ...area, name: editAreaName.trim() } : area
      ));
      setEditingArea(null);
      setEditAreaName('');
    } catch (error) {
      console.error('Error updating area:', error);
      setError('Failed to update area');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteArea = async (id) => {
    if (!window.confirm('Are you sure you want to delete this area?')) return;
    
    try {
      setIsLoading(true);
      
      // First delete any relationships in areas_prompts
      const { error: relError } = await supabase
        .from('areas_prompts')
        .delete()
        .eq('area_id', id);
        
      if (relError) throw relError;
      
      // Then delete the area
      const { error } = await supabase
        .from('areas')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setAreas(areas.filter(area => area.id !== id));
    } catch (error) {
      console.error('Error deleting area:', error);
      setError('Failed to delete area');
    } finally {
      setIsLoading(false);
    }
  };

  // PROMPT CRUD OPERATIONS
  const selectPrompt = async (prompt) => {
    setSelectedPrompt(prompt);
    setPromptForm({
      name: prompt.name,
      prompt: prompt.prompt || '',
      inverted_image: prompt.inverted_image || false,
      system_prompt: prompt.system_prompt || ''
    });
    await fetchAreasForPrompt(prompt.id);
  };

  const clearPromptForm = () => {
    setSelectedPrompt(null);
    setPromptForm({
      name: '',
      prompt: '',
      inverted_image: false,
      system_prompt: ''
    });
    setSelectedPromptAreas([]);
  };

  const handlePromptFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPromptForm({
      ...promptForm,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const toggleAreaSelection = (areaId) => {
    if (selectedPromptAreas.includes(areaId)) {
      setSelectedPromptAreas(selectedPromptAreas.filter(id => id !== areaId));
    } else {
      setSelectedPromptAreas([...selectedPromptAreas, areaId]);
    }
  };

  const savePrompt = async () => {
    if (!promptForm.name.trim()) {
      setError('Prompt name is required');
      return;
    }
    
    try {
      setIsLoading(true);
      
      let promptId;
      
      if (selectedPrompt) {
        // Update existing prompt
        const { error } = await supabase
          .from('prompts')
          .update({
            name: promptForm.name.trim(),
            prompt: promptForm.prompt,
            inverted_image: promptForm.inverted_image,
            system_prompt: promptForm.system_prompt
          })
          .eq('id', selectedPrompt.id);

        if (error) throw error;
        promptId = selectedPrompt.id;
        
        // Update prompts state
        setPrompts(prompts.map(p => 
          p.id === selectedPrompt.id ? { ...p, ...promptForm } : p
        ));
      } else {
        // Create new prompt
        const { data, error } = await supabase
          .from('prompts')
          .insert([{
            name: promptForm.name.trim(),
            prompt: promptForm.prompt,
            inverted_image: promptForm.inverted_image,
            system_prompt: promptForm.system_prompt
          }])
          .select();

        if (error) throw error;
        promptId = data[0].id;
        
        // Update prompts state
        setPrompts([...prompts, data[0]]);
      }
      
      // Handle area-prompt relationships
      if (promptId) {
        // First delete all existing relationships for this prompt
        const { error: deleteError } = await supabase
          .from('areas_prompts')
          .delete()
          .eq('prompt_id', promptId);
          
        if (deleteError) throw deleteError;
        
        // Then create new relationships based on selectedPromptAreas
        if (selectedPromptAreas.length > 0) {
          const areaPromptRelations = selectedPromptAreas.map(areaId => ({
            prompt_id: promptId,
            area_id: areaId
          }));
          
          const { error: insertError } = await supabase
            .from('areas_prompts')
            .insert(areaPromptRelations);
            
          if (insertError) throw insertError;
        }
      }
      
      clearPromptForm();
    } catch (error) {
      console.error('Error saving prompt:', error);
      setError('Failed to save prompt');
    } finally {
      setIsLoading(false);
    }
  };

  const deletePrompt = async (id) => {
    if (!window.confirm('Are you sure you want to delete this prompt?')) return;
    
    try {
      setIsLoading(true);
      
      // First delete any relationships in areas_prompts
      const { error: relError } = await supabase
        .from('areas_prompts')
        .delete()
        .eq('prompt_id', id);
        
      if (relError) throw relError;
      
      // Then delete the prompt
      const { error } = await supabase
        .from('prompts')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setPrompts(prompts.filter(prompt => prompt.id !== id));
      
      if (selectedPrompt && selectedPrompt.id === id) {
        clearPromptForm();
      }
    } catch (error) {
      console.error('Error deleting prompt:', error);
      setError('Failed to delete prompt');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center">
            <button 
              onClick={() => window.history.back()} 
              className="mr-4 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold text-gray-800">
              Prompt Management <span className="text-sm text-gray-500">v{APP_VERSION}</span>
            </h1>
          </div>
          
          <div className="flex space-x-4">
            <button 
              onClick={() => setActiveTab('areas')}
              className={`px-4 py-2 rounded-md ${activeTab === 'areas' 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              Areas
            </button>
            <button 
              onClick={() => setActiveTab('prompts')}
              className={`px-4 py-2 rounded-md ${activeTab === 'prompts' 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              Prompts
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
            <span className="block sm:inline">{error}</span>
            <button 
              onClick={() => setError(null)}
              className="absolute top-0 bottom-0 right-0 px-4 py-3"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Areas Tab */}
        {activeTab === 'areas' && (
          <div className="bg-white shadow-sm rounded-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-800">Areas</h2>
              
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newArea}
                  onChange={(e) => setNewArea(e.target.value)}
                  placeholder="New area name"
                  className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={addArea}
                  disabled={isLoading || !newArea.trim()}
                  className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 disabled:opacity-50 flex items-center"
                >
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Add
                </button>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {areas.length === 0 ? (
                    <tr>
                      <td colSpan="2" className="px-6 py-4 text-center text-sm text-gray-500">
                        No areas found. Create your first area above.
                      </td>
                    </tr>
                  ) : (
                    areas.map((area) => (
                      <tr key={area.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingArea === area.id ? (
                            <input
                              type="text"
                              value={editAreaName}
                              onChange={(e) => setEditAreaName(e.target.value)}
                              className="border border-gray-300 rounded-md px-3 py-1 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          ) : (
                            <div className="text-sm font-medium text-gray-900">{area.name}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {editingArea === area.id ? (
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={updateArea}
                                className="text-green-600 hover:text-green-900"
                              >
                                <Save className="w-5 h-5" />
                              </button>
                              <button
                                onClick={cancelEditArea}
                                className="text-gray-600 hover:text-gray-900"
                              >
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={() => startEditArea(area)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                <Edit className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => deleteArea(area.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Prompts Tab */}
        {activeTab === 'prompts' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Prompts List */}
            <div className="bg-white shadow-sm rounded-lg p-6 md:col-span-1">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-800">Prompts</h2>
                <button
                  onClick={clearPromptForm}
                  className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 flex items-center"
                >
                  <PlusCircle className="w-4 h-4 mr-2" />
                  New
                </button>
              </div>
              
              <div className="mb-4">
                <input
                  type="text"
                  value={promptFilter}
                  onChange={(e) => setPromptFilter(e.target.value)}
                  placeholder="Filter prompts..."
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="space-y-2 max-h-[calc(100vh)] overflow-y-auto">
                {prompts.length === 0 ? (
                  <div className="text-center text-sm text-gray-500 py-4">
                    No prompts found. Create your first prompt using the form.
                  </div>
                ) : (
                  prompts
                    .filter(prompt => prompt.name.toLowerCase().includes(promptFilter.toLowerCase()))
                    .map((prompt) => (
                    <div 
                      key={prompt.id}
                      onClick={() => selectPrompt(prompt)}
                      className={`p-3 rounded-md cursor-pointer ${
                        selectedPrompt && selectedPrompt.id === prompt.id
                          ? 'bg-blue-100 border border-blue-300'
                          : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div className="font-medium">{prompt.name}</div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deletePrompt(prompt.id);
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* Prompt Form */}
            <div className="bg-white shadow-sm rounded-lg p-6 md:col-span-2">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">
                {selectedPrompt ? 'Edit Prompt' : 'New Prompt'}
              </h2>
              <VariableInfo />
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={promptForm.name}
                    onChange={handlePromptFormChange}
                    placeholder="Prompt name"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prompt
                  </label>
                  <textarea
                    name="prompt"
                    value={promptForm.prompt}
                    onChange={handlePromptFormChange}
                    placeholder="Enter prompt content"
                    rows="4"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    System Prompt
                  </label>
                  <textarea
                    name="system_prompt"
                    value={promptForm.system_prompt}
                    onChange={handlePromptFormChange}
                    placeholder="Enter system prompt"
                    rows="4"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <div className="flex items-center">
                    <input
                      id="inverted-image-checkbox"
                      type="checkbox"
                      name="inverted_image"
                      checked={promptForm.inverted_image}
                      onChange={(e) => setPromptForm({
                        ...promptForm,
                        inverted_image: e.target.checked
                      })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="inverted-image-checkbox" className="ml-2 block text-sm font-medium text-gray-700">
                      Inverted Image
                    </label>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Associated Areas
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {areas.map((area) => (
                      <div key={area.id} className="flex items-center">
                        <input
                          type="checkbox"
                          id={`area-${area.id}`}
                          checked={selectedPromptAreas.includes(area.id)}
                          onChange={() => toggleAreaSelection(area.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor={`area-${area.id}`}
                          className="ml-2 block text-sm text-gray-900"
                        >
                          {area.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="pt-4 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={clearPromptForm}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={savePrompt}
                    disabled={isLoading || !promptForm.name.trim()}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {isLoading ? 'Saving...' : 'Save Prompt'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
